import { Compass, Bookmark, Settings, SlidersHorizontal, Crown } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';

type Tab = 'explore' | 'myTrips' | 'filters' | 'settings' | 'pro';

interface BottomTabBarProps {
  activeTab: Tab;
  onTabChange: (tab: Tab) => void;
  showMyTrips: boolean;
}

export function BottomTabBar({ activeTab, onTabChange, showMyTrips }: BottomTabBarProps) {
  const { t } = useLanguage();

  const tabs: { id: Tab; label: string; icon: typeof Compass; gold?: boolean }[] = [
    { id: 'explore', label: t.common.explore, icon: Compass },
    { id: 'myTrips', label: t.common.myTrips, icon: Bookmark },
    { id: 'filters', label: t.settings.filters || 'Filters', icon: SlidersHorizontal },
    { id: 'settings', label: t.common.settings, icon: Settings },
    { id: 'pro', label: 'Pro', icon: Crown, gold: true },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-20 bg-[var(--bg-elevated)]/95 backdrop-blur-md border-t border-[var(--border)] shadow-[0_-2px_12px_rgba(0,0,0,0.06)]">
      <div className="max-w-2xl mx-auto flex items-center justify-around px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        {tabs.map(({ id, label, icon: Icon, gold }) => {
          const isActive = activeTab === id;
          const showBadge = id === 'myTrips' && showMyTrips;

          return (
            <button
              key={id}
              onClick={() => onTabChange(id)}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors min-w-[48px] ${
                isActive
                  ? gold ? 'text-[var(--accent-gold)] bg-[var(--accent-gold)]/15' : 'text-[var(--accent-teal)] bg-[var(--accent-teal)]/15'
                  : gold ? 'text-[var(--accent-gold)]/50 hover:text-[var(--accent-gold)]' : 'text-[var(--text-secondary)] hover:text-[var(--accent-teal)]'
              }`}
            >
              <div className="relative">
                <Icon className="w-5 h-5" />
                {showBadge && !isActive && (
                  <div className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[var(--accent-gold)]" />
                )}
              </div>
              <span className="text-[10px] font-medium">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
